import { useLayoutEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';

export type TomTile = 'primary' | 'success' | 'warning';

const ICONE_TOM: Record<TomTile, string> = {
  primary: 'bg-primary-50 text-primary-600',
  success: 'bg-success-100 text-success-600',
  warning: 'bg-warning-100 text-warning-700',
};

// Número que nunca corta: começa num tamanho fluido (clamp) e, se estourar a
// largura do tile, encolhe 1px por vez (até 16px) em vez de mostrar "…".
export function NumeroFita({
  valor,
  base,
  className = '',
  quebrar = false,
}: {
  valor: string;
  base?: string;
  className?: string;
  quebrar?: boolean;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [px, setPx] = useState<number | null>(null);

  // Enquanto o número estourar a largura, reduz 1px por vez (até 16px).
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (el.scrollWidth - el.clientWidth > 0) {
      const atual = parseFloat(window.getComputedStyle(el).fontSize);
      if (atual > 16) setPx(Math.max(16, Math.floor(atual - 1)));
    }
  }, [px]);

  // Se o tile alargar (girar/redimensionar), deixa o número crescer de novo.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const pai = el.parentElement;
    if (!pai) return;
    let largura = pai.clientWidth;
    const ro = new ResizeObserver(() => {
      const nova = pai.clientWidth;
      if (nova > largura + 1) setPx(null);
      largura = nova;
    });
    ro.observe(pai);
    return () => ro.disconnect();
  }, []);

  return (
    <span
      ref={ref}
      style={px != null ? { fontSize: `${px}px` } : undefined}
      className={`block font-extrabold leading-none tabular-nums ${
        quebrar ? 'whitespace-normal' : 'whitespace-nowrap'
      } ${
        base ?? 'text-[clamp(18px,6vw,28px)] md:text-[clamp(20px,1.8vw,28px)]'
      } ${className}`}
    >
      {/* Quando o valor muda, o número rola para cima em 200 ms. Só
          transform/opacity — a largura do tile não muda. */}
      <motion.span
        key={valor}
        className="inline-block"
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
      >
        {valor}
      </motion.span>
    </span>
  );
}

export function TilePainel({
  icone,
  tom,
  valor,
  legenda,
  apoio,
  destaqueTudoPago = false,
  valorCls,
  valorBase,
  valorQuebra = false,
  aoClicar,
}: {
  icone: string;
  tom: TomTile;
  valor: string;
  legenda: string;
  apoio?: string;
  destaqueTudoPago?: boolean;
  valorCls?: string;
  /** Sobrescreve o tamanho da fonte do número (ex.: texto no lugar de valor). */
  valorBase?: string;
  /** Permite quebrar em duas linhas em vez de encolher até sumir. */
  valorQuebra?: boolean;
  aoClicar?: () => void;
}) {
  const classe = 'min-w-0 rounded-3xl bg-white p-4 shadow-[0_6px_18px_rgba(20,25,60,.06)]';
  const corpo = (
    <>
      <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${ICONE_TOM[tom]}`}>
        <i className={`${icone} text-lg`} />
      </span>
      {destaqueTudoPago ? (
        <span className="mt-3 block text-[18px] font-bold leading-tight text-success-600">
          {valor}
        </span>
      ) : (
        <NumeroFita
          key={valor}
          valor={valor}
          base={valorBase}
          quebrar={valorQuebra}
          className={`mt-3 ${valorCls ?? 'text-foreground-950'}`}
        />
      )}
      <span className="mt-1.5 block text-[13px] leading-snug text-foreground-500">{legenda}</span>
      {apoio && (
        <span className="mt-0.5 block text-[12px] leading-snug text-foreground-400">{apoio}</span>
      )}
    </>
  );

  if (aoClicar) {
    return (
      <button
        type="button"
        onClick={aoClicar}
        className={`${classe} block w-full cursor-pointer text-left transition-colors hover:bg-primary-50`}
      >
        {corpo}
      </button>
    );
  }
  return <div className={classe}>{corpo}</div>;
}